import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep } from '../../atoms';
import { useAtom } from 'jotai';
import { stepConfigAtom } from '../../atoms';
import PageMeta from 'components/PageMeta/PageMeta';
import { getSpouseTitle } from './constants';
import DocumentationRequirementCard from '../../__components/DocumentationRequirementCard';
import { combineDocumentationRequirements, FAMILY_FILE_TYPES } from 'services/family.service';
import { CardTitle } from 'components/Card/Card';
import { getOtherAdultFirstName } from '../../utils';
import { CheckmarkFilled } from '@carbon/icons-react';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import FamilyAppBlankFormDownloadButton from '../../__components/FamilyAppBlankFormDownloadButton';

export function FamilyAppSpouseDocumentationRequirements({ application, setStep, loading, readOnly = false }) {
  const [stepConfig] = useAtom(stepConfigAtom);
  const spouseTitle = getSpouseTitle(application);

  // Load application files
  const [files, setFiles] = useState(null);
  const [documentationRequirements, setDocumentationRequirements] = useState(null);

  useEffect(() => {
    if (application?.id) {
      familyService.getApplicationFiles(application.id).then((applicationFiles) => {
        setFiles(applicationFiles || []);
      });
    }
  }, [application?.id]);

  // Load spouse documentation requirements
  useEffect(() => {
    if (application?.id && application?.additionalParents?.[0]?.id) {
      familyService
        .getSpouseDocumentationRequirements(application.id, application.additionalParents[0].id)
        .then((requirements) => {
          const _combinedRequirements = combineDocumentationRequirements(requirements);
          setDocumentationRequirements(_combinedRequirements);
        })
        .catch((error) => {
          console.error('Failed to load spouse documentation requirements:', error);
          setDocumentationRequirements([]);
        });
    }
  }, [application?.id, application?.additionalParents]);

  if (loading || files === null || documentationRequirements === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title={`${spouseTitle.charAt(0).toUpperCase() + spouseTitle.slice(1)} Documentation Requirements`} />

      <div className="p-8">
        <Banner
          question={
            documentationRequirements?.length === 0
              ? `No documents needed for ${getOtherAdultFirstName(application?.additionalParents?.[0])}`
              : `${getOtherAdultFirstName(application?.additionalParents?.[0])}'s documents we’ll need`
          }
          guidance={
            <>
              {documentationRequirements?.length === 0 && (
                <CheckmarkFilled className="w-5 h-5 mb-4 inline-block align-middle mb-[3px] mr-2" />
              )}

              {documentationRequirements?.length === 0
                ? `Based on your answers, we don't need any documents for ${getOtherAdultFirstName(
                    application?.additionalParents?.[0],
                  )} at this time.`
                : "Based on your answers, here's what you'll need"}
            </>
          }
          className="mb-12"
        />

        {/* Dynamic Documentation Cards */}
        {documentationRequirements?.map((requirement, index) => (
          <DocumentationRequirementCard
            key={requirement.fileType}
            requirement={requirement}
            files={files}
            setFiles={setFiles}
            application={application}
            readOnly={readOnly}
            title={<CardTitle title={requirement.title} icon="📋" />}
            showRequirementStatus
            className={index > 0 ? 'mt-10' : ''}
            uploadFileProps={
              requirement.fileType === FAMILY_FILE_TYPES.SPOUSE_WORK?.code
                ? {
                    uploadButtonText: 'Upload form(s)',
                    preUploadButtonContent: <FamilyAppBlankFormDownloadButton fileType={requirement.fileType} />,
                  }
                : {}
            }
          />
        ))}
      </div>

      <ApplicationWizardActions
        applicationIsReadOnly={readOnly}
        backButtonProps={{
          onClick: () => {
            const previousStep = getPreviousStep(
              FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS,
              application,
              stepConfig,
            );
            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
          'data-testid': 'spouse-documentation-requirements-back-button',
        }}
        nextButtonProps={{
          text: 'Next',
          onClick: () => {
            const nextStep = getNextStep(
              FAMILY_APPLICATION_STEPS.SPOUSE_DOCUMENTATION_REQUIREMENTS,
              application,
              stepConfig,
              0,
            );
            setStep({
              newStep: nextStep,
              applicationId: application?.id,
            });
          },
          'data-testid': 'spouse-documentation-requirements-next-button',
        }}
      />
    </>
  );
}
